import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`);
      const data = await response.json();
      
      setPaymentInfo(data);

      if (data.payment_status === 'paid') {
        setStatus('success');
        return;
      } else if (data.status === 'expired' || data.status === 'canceled') {
        setStatus('error');
        return;
      }

      // Continuar polling se ainda está pendente
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (error) {
      console.error('Erro ao verificar status:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getPlanName = (plan) => {
    const plans = {
      monthly: 'Mensal',
      yearly: 'Anual'
    };
    return plans[plan] || plan;
  };

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6">
            <div className="text-center">
              <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Processando pagamento...</h2>
              <p className="text-muted-foreground">Aguarde enquanto confirmamos seu pagamento.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Pagamento não confirmado</h2>
              <p className="text-muted-foreground mb-6">
                Não foi possível confirmar seu pagamento. Se você realizou o pagamento, aguarde alguns minutos e tente fazer login novamente.
              </p>
              <div className="space-y-2">
                <Button onClick={() => navigate('/login')} className="w-full" data-testid="back-to-login-btn">
                  Voltar ao Login
                </Button>
                <Button variant="outline" onClick={() => window.location.reload()} className="w-full">
                  Tentar Novamente
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Card className="w-full max-w-md mx-4">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <CardTitle className="text-2xl">Pagamento Confirmado!</CardTitle>
          <CardDescription>
            Obrigado! Seu acesso foi liberado.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentInfo && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Plano:</span>
                  <span className="font-medium">{getPlanName(paymentInfo.plan)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Valor:</span>
                  <span className="font-medium">{formatCurrency(paymentInfo.amount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium text-emerald-600">Pago</span>
                </div>
              </div>
            </div>
          )}
          
          <Button 
            onClick={() => navigate('/login')} 
            className="w-full"
            size="lg"
            data-testid="access-system-btn"
          >
            Acessar o Sistema
          </Button>
          
          <p className="text-xs text-muted-foreground text-center mt-4">
            Um recibo foi enviado para seu e-mail cadastrado.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
